import { useEffect, useMemo, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { toast } from "@/common/elements/sonner";
import { Button } from "@/common/elements/button";
import { Skeleton } from "@/common/elements/skeleton";
import { ApiSession } from "@/common/auth/utils";
import { AuthenticatedUser } from "@/common/keycloak/keycloak";
import { useSelectedCustomerId } from "@/common/hooks/use-selected-customer-id";
import {
  useAddHarnessCustomerPaymentMethodMutation,
  useDeleteHarnessCustomerPaymentMethodMutation,
  useGetHarnessPaymentCountriesQuery,
  useGetHarnessPaymentStatesQuery,
  useGetHarnessCustomerPaymentMethodsQuery,
  useGetHarnessPaymentTokenizationDetailQuery,
  useUpdateHarnessCustomerPaymentMethodMutation,
} from "@/api/providers/payment-methods";
import {
  UiPaymentMethodType,
  mapHarnessPaymentMethodToUi,
  type UiPaymentMethod,
} from "@/plugins/controls/common/billing-mappers";
import {
  CardCategoryEnum,
  UNSUPPORTED_UNKNOWN_CARD_MESSAGE,
  isUnknownCard,
  normalizeCardCategoryLoose,
  parseAllowUnknownCardTypeProcessing,
  parsePositiveSurchargePercent,
} from "@/plugins/controls/common/billing-payment-helpers";
import { PaymentMethodItem } from "@/plugins/controls/payment-methods-control/payment-method-item/PaymentMethodItem";
import {
  PaymentMethodsDrawer,
  PaymentMethodsDrawerMode,
} from "@/plugins/controls/payment-methods-control/payment-methods-drawer/PaymentMethodsDrawer";
import {
  createEmptyMethodForm,
  getCollectMethodFormErrors,
  isCollectMethodFormValid,
  toAddPaymentMethodRequest,
  toMethodFormState,
  toUpdatePaymentMethodRequest,
  type MethodFormState,
} from "@/plugins/controls/payment-methods-control/utils";
import { type CollectJsTokenizedPayment } from "@/plugins/controls/payment-methods-control/payment-methods-drawer/collect-js-payment-form/CollectJsPaymentForm";
import { PaymentMethodTypeEnum } from "@/plugins/stub/billing-data/utils";
import {
  getDefaultPaymentCountryId,
  normalizePostalCode,
  type BillingAddressValidationErrors,
} from "@/plugins/controls/common/billing-address";

export function PaymentMethodsControl(_props: {
  authUser: AuthenticatedUser;
  apiSession: ApiSession | null;
  onLogout: () => Promise<void>;
}) {
  const selectedCustomerId = useSelectedCustomerId();
  const paymentMethodsQueryArg =
    selectedCustomerId !== null
      ? { customerId: selectedCustomerId }
      : skipToken;

  const {
    data: paymentMethodData = [],
    isLoading: paymentMethodsLoading,
    isFetching: paymentMethodsFetching,
    isError: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useGetHarnessCustomerPaymentMethodsQuery(paymentMethodsQueryArg);

  const methods = useMemo(
    () =>
      paymentMethodData.map((method) => mapHarnessPaymentMethodToUi(method)),
    [paymentMethodData],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<PaymentMethodsDrawerMode>(
    PaymentMethodsDrawerMode.Create,
  );
  const [activeMethodId, setActiveMethodId] = useState<number | null>(null);
  const [methodForm, setMethodForm] = useState<MethodFormState>(
    createEmptyMethodForm(),
  );
  const [collectTokenRequestNonce, setCollectTokenRequestNonce] = useState(0);
  const [, setCollectReady] = useState(false);
  const [collectError, setCollectError] = useState<string | null>(null);
  const [newMethodCardCategory, setNewMethodCardCategory] = useState<
    string | null
  >(null);
  const [validationErrors, setValidationErrors] =
    useState<BillingAddressValidationErrors>({});
  const [createSubmitPending, setCreateSubmitPending] = useState(false);
  const createSubmitPendingRef = useRef(false);
  const createSubmitTimeoutRef = useRef<number | null>(null);

  const { data: tokenizationDetail, isLoading: tokenizationLoading } =
    useGetHarnessPaymentTokenizationDetailQuery();
  const {
    data: paymentCountries = [],
    isLoading: paymentCountriesLoading,
    isFetching: paymentCountriesFetching,
  } = useGetHarnessPaymentCountriesQuery();
  const paymentStatesQueryArg =
    drawerMode === PaymentMethodsDrawerMode.Create &&
    methodForm.countryId !== null
      ? { countryId: methodForm.countryId }
      : skipToken;
  const {
    data: paymentStates = [],
    isLoading: paymentStatesLoading,
    isFetching: paymentStatesFetching,
  } = useGetHarnessPaymentStatesQuery(paymentStatesQueryArg);
  const [addCustomerPaymentMethod, { isLoading: addPaymentMethodLoading }] =
    useAddHarnessCustomerPaymentMethodMutation();
  const [
    updateCustomerPaymentMethod,
    { isLoading: updatePaymentMethodLoading },
  ] = useUpdateHarnessCustomerPaymentMethodMutation();
  const [
    deleteCustomerPaymentMethod,
    { isLoading: deletePaymentMethodLoading },
  ] = useDeleteHarnessCustomerPaymentMethodMutation();

  const activeMethod =
    methods.find((method) => method.id === activeMethodId) ?? null;
  const isMethodsBusy = paymentMethodsLoading || paymentMethodsFetching;
  const defaultCountryId = useMemo(
    () => getDefaultPaymentCountryId(paymentCountries),
    [paymentCountries],
  );
  const surchargePercent = useMemo(() => {
    const candidate = tokenizationDetail?.creditCardSurchargePercent ?? null;
    return parsePositiveSurchargePercent(candidate);
  }, [tokenizationDetail]);
  const showSurchargeNotice =
    drawerMode === PaymentMethodsDrawerMode.Create &&
    methodForm.type === UiPaymentMethodType.Card &&
    normalizeCardCategoryLoose(newMethodCardCategory) ===
      CardCategoryEnum.Credit &&
    surchargePercent !== null;
  const allowUnknownCardTypeProcessing = parseAllowUnknownCardTypeProcessing(
    tokenizationDetail?.allowUnknownCardTypeProcessing,
  );

  function clearCreateSubmitTimeout() {
    if (createSubmitTimeoutRef.current !== null) {
      window.clearTimeout(createSubmitTimeoutRef.current);
      createSubmitTimeoutRef.current = null;
    }
  }

  useEffect(() => {
    createSubmitPendingRef.current = createSubmitPending;
  }, [createSubmitPending]);

  useEffect(() => {
    return () => {
      clearCreateSubmitTimeout();
    };
  }, []);

  useEffect(() => {
    if (
      drawerMode !== PaymentMethodsDrawerMode.Create ||
      !drawerOpen ||
      methodForm.countryId !== null ||
      defaultCountryId === null
    ) {
      return;
    }

    setMethodForm((current) => ({ ...current, countryId: defaultCountryId }));
  }, [defaultCountryId, drawerMode, drawerOpen, methodForm.countryId]);

  useEffect(() => {
    if (
      drawerMode !== PaymentMethodsDrawerMode.Create ||
      methodForm.stateId === null
    ) {
      return;
    }

    if (paymentStates.some((state) => state.id === methodForm.stateId)) {
      return;
    }

    setMethodForm((current) => ({ ...current, stateId: null }));
  }, [drawerMode, methodForm.stateId, paymentStates]);

  function updateMethodForm(nextValue: MethodFormState) {
    setMethodForm(nextValue);
    setValidationErrors({});
  }

  function openCreateDrawer() {
    clearCreateSubmitTimeout();
    setDrawerMode(PaymentMethodsDrawerMode.Create);
    setActiveMethodId(null);
    setMethodForm(createEmptyMethodForm());
    setCollectError(null);
    setNewMethodCardCategory(null);
    setValidationErrors({});
    setCollectReady(false);
    setCreateSubmitPending(false);
    setDrawerOpen(true);
  }

  function openEditDrawer(method: UiPaymentMethod) {
    clearCreateSubmitTimeout();
    setDrawerMode(PaymentMethodsDrawerMode.Edit);
    setActiveMethodId(method.id);
    setMethodForm(toMethodFormState(method));
    setCollectError(null);
    setNewMethodCardCategory(null);
    setValidationErrors({});
    setCollectReady(false);
    setCreateSubmitPending(false);
    setDrawerOpen(true);
  }

  function onDrawerOpenChange(open: boolean) {
    setDrawerOpen(open);

    if (!open) {
      clearCreateSubmitTimeout();
      setActiveMethodId(null);
      setCollectError(null);
      setNewMethodCardCategory(null);
      setValidationErrors({});
      setCollectReady(false);
      setCreateSubmitPending(false);
    }
  }

  function setMethodType(type: UiPaymentMethodType) {
    clearCreateSubmitTimeout();
    setMethodForm((current) => ({
      ...createEmptyMethodForm(type),
      firstName: current.firstName,
      lastName: current.lastName,
      company: current.company,
      address1: current.address1,
      address2: current.address2,
      city: current.city,
      stateId: current.stateId,
      postalCode: current.postalCode,
      countryId: current.countryId,
      phone: current.phone,
      fax: current.fax,
      isDefault: current.isDefault,
      disabled: current.disabled,
    }));
    setCollectError(null);
    setNewMethodCardCategory(null);
    setValidationErrors({});
    setCollectReady(false);
  }

  async function saveTokenizedMethod(
    tokenizedPayment: CollectJsTokenizedPayment,
  ) {
    clearCreateSubmitTimeout();

    if (!createSubmitPendingRef.current) {
      return;
    }

    if (selectedCustomerId === null) {
      setCreateSubmitPending(false);
      toast.error("Select a customer before adding a payment method.");
      return;
    }

    if (
      !allowUnknownCardTypeProcessing &&
      tokenizedPayment.paymentMethodType === PaymentMethodTypeEnum.Card &&
      isUnknownCard(tokenizedPayment.cardCategory)
    ) {
      setCreateSubmitPending(false);
      setCollectError(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      toast.error(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
      return;
    }

    try {
      await addCustomerPaymentMethod({
        customerId: selectedCustomerId,
        body: toAddPaymentMethodRequest(methodForm, tokenizedPayment),
      }).unwrap();
      await refetchPaymentMethods();

      setDrawerOpen(false);
      setMethodForm(createEmptyMethodForm(methodForm.type));
      setCreateSubmitPending(false);
      setCollectError(null);
      setValidationErrors({});

      toast.success(
        "Payment method added. There are no outstanding invoices to charge right now.",
      );
    } catch {
      setCreateSubmitPending(false);
      clearCreateSubmitTimeout();
      toast.error("Could not save the payment method. Please try again.");
    }
  }

  async function submitMethod() {
    if (drawerMode === PaymentMethodsDrawerMode.Create) {
      const normalizedForm = {
        ...methodForm,
        postalCode: normalizePostalCode(methodForm.postalCode),
      };

      updateMethodForm(normalizedForm);

      const nextValidationErrors = getCollectMethodFormErrors(
        normalizedForm,
        paymentStates.length > 0,
      );

      if (!isCollectMethodFormValid(normalizedForm, paymentStates.length > 0)) {
        setValidationErrors(nextValidationErrors);
        toast.error(
          "Complete all required fields before adding this payment method.",
        );
        return;
      }

      if (selectedCustomerId === null) {
        toast.error("Select a customer before adding a payment method.");
        return;
      }

      if (
        !allowUnknownCardTypeProcessing &&
        methodForm.type === UiPaymentMethodType.Card &&
        isUnknownCard(newMethodCardCategory)
      ) {
        setCollectError(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
        toast.error(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
        return;
      }

      setCreateSubmitPending(true);
      setCollectError(null);
      clearCreateSubmitTimeout();
      createSubmitTimeoutRef.current = window.setTimeout(() => {
        if (!createSubmitPendingRef.current) {
          return;
        }

        setCreateSubmitPending(false);
        setCollectError(
          "Secure payment fields did not respond. Please check details and try again.",
        );
        toast.error(
          "Secure payment fields did not respond. Please check details and try again.",
        );
      }, 60000);
      setCollectTokenRequestNonce((current) => current + 1);
      return;
    }

    if (!activeMethodId) {
      return;
    }

    if (selectedCustomerId === null || !activeMethod) {
      return;
    }

    if (methodForm.disabled && methodForm.isDefault) {
      toast.error("A disabled payment method cannot be set as the default.");
      return;
    }

    const updateRequest = toUpdatePaymentMethodRequest(
      activeMethod,
      methodForm,
    );

    if (Object.keys(updateRequest).length === 0) {
      setDrawerOpen(false);
      setActiveMethodId(null);
      return;
    }

    try {
      await updateCustomerPaymentMethod({
        customerId: selectedCustomerId,
        paymentMethodId: activeMethodId,
        body: updateRequest,
      }).unwrap();

      await refetchPaymentMethods();
      setDrawerOpen(false);
      setActiveMethodId(null);
      toast.success("Payment method updated.");
    } catch {
      toast.error("Could not update the payment method. Please try again.");
    }
  }

  async function deleteMethod() {
    if (!activeMethodId) {
      return;
    }

    if (methods.length <= 1) {
      toast.error("At least one payment method is required for AutoPay.");
      return;
    }

    const methodToDelete = methods.find(
      (method) => method.id === activeMethodId,
    );

    if (!methodToDelete || selectedCustomerId === null) {
      return;
    }

    const confirmed = window.confirm(
      methodToDelete.isDefault
        ? "Remove this default payment method? Another saved method will become the default for AutoPay."
        : "Remove this payment method?",
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteCustomerPaymentMethod({
        customerId: selectedCustomerId,
        paymentMethodId: activeMethodId,
      }).unwrap();

      await refetchPaymentMethods();
      setDrawerOpen(false);
      setActiveMethodId(null);
      toast.success(
        methodToDelete.isDefault
          ? "Payment method removed. Another saved method is now the default for AutoPay."
          : "Payment method removed.",
      );
    } catch {
      toast.error("Could not remove the payment method. Please try again.");
    }
  }

  return (
    <section className="sm:rounded-lg sm:border sm:bg-card sm:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-lg font-semibold">Payment Methods</h2>
        <Button
          onClick={openCreateDrawer}
          disabled={selectedCustomerId === null || isMethodsBusy}
        >
          Add payment method
        </Button>
      </div>
      <p className="mt-6 md:mt-2 text-sm text-muted-foreground">
        Add, update, and remove payment methods. Your default payment method
        will be charged automatically on your invoice due dates.
      </p>
      <div className="mt-6 space-y-3">
        {selectedCustomerId === null ? (
          <p className="rounded-md border bg-muted/20 p-4 text-sm text-muted-foreground">
            Select a customer to manage payment methods.
          </p>
        ) : paymentMethodsError ? (
          <p className="rounded-md border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
            Could not load saved payment methods. Please refresh and try again.
          </p>
        ) : isMethodsBusy ? (
          <div className="space-y-3" aria-label="Loading payment methods">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`payment-method-skeleton-${index}`}
                className="rounded-md border bg-muted/20 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-44" />
                    <Skeleton className="h-4 w-60" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : methods.length === 0 ? (
          <p className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 bg-muted/20 p-4 text-sm text-muted-foreground">
            Please add a payment method to your account.
          </p>
        ) : (
          methods.map((method) => (
            <PaymentMethodItem
              key={method.id}
              method={method}
              onEdit={openEditDrawer}
            />
          ))
        )}
      </div>

      <PaymentMethodsDrawer
        open={drawerOpen}
        mode={drawerMode}
        methodPreview={activeMethod}
        form={methodForm}
        countryOptions={paymentCountries}
        stateOptions={paymentStates}
        validationErrors={validationErrors}
        isCountriesLoading={paymentCountriesLoading || paymentCountriesFetching}
        isStatesLoading={paymentStatesLoading || paymentStatesFetching}
        collectJsConfig={tokenizationDetail ?? null}
        collectTokenRequestNonce={collectTokenRequestNonce}
        collectDisabled={
          tokenizationLoading ||
          addPaymentMethodLoading ||
          updatePaymentMethodLoading ||
          deletePaymentMethodLoading ||
          (drawerMode === PaymentMethodsDrawerMode.Create &&
            createSubmitPending)
        }
        collectError={collectError}
        canDelete={methods.length > 1}
        onOpenChange={onDrawerOpenChange}
        onMethodTypeChange={setMethodType}
        onFormChange={updateMethodForm}
        onCollectReadyChange={setCollectReady}
        onCollectCardCategoryChange={(category) => {
          const normalizedCategory = normalizeCardCategoryLoose(category);
          setNewMethodCardCategory(normalizedCategory);

          if (
            !allowUnknownCardTypeProcessing &&
            methodForm.type === UiPaymentMethodType.Card &&
            isUnknownCard(normalizedCategory)
          ) {
            setCollectError(UNSUPPORTED_UNKNOWN_CARD_MESSAGE);
            return;
          }

          setCollectError(null);
        }}
        surchargeNoticePercent={surchargePercent}
        showSurchargeNotice={showSurchargeNotice}
        onCollectTokenized={saveTokenizedMethod}
        onCollectError={(message) => {
          clearCreateSubmitTimeout();
          setCreateSubmitPending(false);
          setCollectError(message);
          toast.error(message);
        }}
        onSubmit={submitMethod}
        onCancel={() => onDrawerOpenChange(false)}
        onDelete={deleteMethod}
      />
    </section>
  );
}
